// Thin fetch wrapper. The transport intentionally knows nothing about Clerk
// or react-query — both are wired in `providers.tsx`. Errors are normalized
// so query hooks can branch on `error.status` instead of probing a Response.

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly body: unknown;

  constructor(status: number, message: string, opts?: { code?: string; body?: unknown }) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = opts?.code;
    this.body = opts?.body;
  }
}

export interface ApiClientOptions {
  readonly baseUrl: string;
  readonly getToken: () => Promise<string | null>;
}

export interface ApiClient {
  get<T>(path: string, init?: RequestInit): Promise<T>;
  post<T>(path: string, body: unknown, init?: RequestInit): Promise<T>;
  patch<T>(path: string, body: unknown, init?: RequestInit): Promise<T>;
  del<T>(path: string, init?: RequestInit): Promise<T>;
}

interface ApiErrorBody {
  readonly code?: string;
  readonly message?: string;
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  return typeof value === 'object' && value !== null;
}

async function parseError(res: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // Non-JSON response — fall through with body=null.
  }
  const code = isApiErrorBody(body) && typeof body.code === 'string' ? body.code : undefined;
  const message =
    isApiErrorBody(body) && typeof body.message === 'string'
      ? body.message
      : `request failed with status ${String(res.status)}`;
  return new ApiError(res.status, message, { ...(code !== undefined ? { code } : {}), body });
}

export function createApiClient(opts: ApiClientOptions): ApiClient {
  async function request<T>(path: string, init: RequestInit): Promise<T> {
    const token = await opts.getToken();
    const headers = new Headers(init.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    if (init.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    const res = await fetch(`${opts.baseUrl}${path}`, { ...init, headers });
    if (!res.ok) throw await parseError(res);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  return {
    get: (path, init) => request(path, { ...init, method: 'GET' }),
    post: (path, body, init) =>
      request(path, { ...init, method: 'POST', body: JSON.stringify(body) }),
    patch: (path, body, init) =>
      request(path, { ...init, method: 'PATCH', body: JSON.stringify(body) }),
    del: (path, init) => request(path, { ...init, method: 'DELETE' }),
  };
}
